import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import api from "../api/axios";
import { useAuth } from "../context/AuthContext";
import "../css/PromotionalPopup.css";

const GUEST_INTERVAL_MS = 15 * 60 * 1000;
const GUEST_STORAGE_PREFIX = "promotion:last-shown:";
const HIDDEN_PATH_PREFIXES = [
  "/checkout",
  "/order-success",
  "/order-cancelled",
];

const getGuestStorageKey = (promotionId) =>
  `${GUEST_STORAGE_PREFIX}${promotionId}`;

const readGuestTimestamp = (promotionId) => {
  try {
    return Number(localStorage.getItem(getGuestStorageKey(promotionId))) || 0;
  } catch {
    return 0;
  }
};

const writeGuestTimestamp = (promotionId) => {
  try {
    localStorage.setItem(getGuestStorageKey(promotionId), String(Date.now()));
  } catch {
    // The in-memory session still prevents repeated display until reload.
  }
};

const PromotionalPopup = () => {
  const location = useLocation();
  const { user, authLoading } = useAuth();
  const [promotion, setPromotion] = useState(null);
  const [visible, setVisible] = useState(false);
  const guestTimerRef = useRef(null);

  const isRegistered = Boolean(user?.user_id || user?.id);
  const isHiddenRoute = useMemo(
    () =>
      HIDDEN_PATH_PREFIXES.some(
        (path) =>
          location.pathname === path || location.pathname.startsWith(`${path}/`)
      ),
    [location.pathname]
  );

  const clearGuestTimer = useCallback(() => {
    if (guestTimerRef.current) {
      window.clearTimeout(guestTimerRef.current);
      guestTimerRef.current = null;
    }
  }, []);

  const acknowledge = useCallback(
    async (action, buttonIndex = null) => {
      if (!isRegistered || !promotion?.promotionId) return;

      try {
        await api.post(
          `/customer/promotions/${promotion.promotionId}/acknowledge`,
          { action, buttonIndex }
        );
      } catch (error) {
        console.error("Promotion acknowledgement failed:", error);
      }
    },
    [isRegistered, promotion?.promotionId]
  );

  const fetchPromotion = useCallback(async () => {
    if (authLoading || isHiddenRoute) return;

    try {
      const endpoint = isRegistered
        ? "/customer/promotions/active"
        : "/promotions/active";
      const response = await api.get(endpoint);
      const nextPromotion = response.data?.data || null;

      clearGuestTimer();

      if (!nextPromotion) {
        setPromotion(null);
        setVisible(false);
        return;
      }

      if (!isRegistered) {
        const lastShown = readGuestTimestamp(nextPromotion.promotionId);
        const remaining = GUEST_INTERVAL_MS - (Date.now() - lastShown);

        if (remaining > 0) {
          setPromotion(nextPromotion);
          setVisible(false);
          guestTimerRef.current = window.setTimeout(() => {
            writeGuestTimestamp(nextPromotion.promotionId);
            setVisible(true);
          }, remaining);
          return;
        }

        writeGuestTimestamp(nextPromotion.promotionId);
      }

      setPromotion(nextPromotion);
      setVisible(true);
    } catch (error) {
      // Promotions are non-essential and must never interrupt shopping.
      console.error("Promotion fetch failed:", error);
    }
  }, [authLoading, clearGuestTimer, isHiddenRoute, isRegistered]);

  useEffect(() => {
    setPromotion(null);
    setVisible(false);
    fetchPromotion();

    return clearGuestTimer;
  }, [clearGuestTimer, fetchPromotion]);

  const closePopup = useCallback(() => {
    setVisible(false);

    if (isRegistered) {
      acknowledge("read");
      setPromotion(null);
      return;
    }

    clearGuestTimer();
    guestTimerRef.current = window.setTimeout(() => {
      if (!isHiddenRoute) {
        writeGuestTimestamp(promotion?.promotionId);
        setVisible(true);
      }
    }, GUEST_INTERVAL_MS);
  }, [acknowledge, clearGuestTimer, isHiddenRoute, isRegistered, promotion]);

  useEffect(() => {
    if (!visible) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePopup();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePopup, visible]);

  const followLink = async (url, action, buttonIndex = null) => {
    if (!url) return;

    setVisible(false);
    if (isRegistered) {
      await acknowledge(action, buttonIndex);
    } else if (promotion?.promotionId) {
      writeGuestTimestamp(promotion.promotionId);
    }

    window.location.assign(url);
  };

  if (!promotion || !visible || isHiddenRoute) return null;

  return (
    <div className="promotion-popup-backdrop" onMouseDown={closePopup}>
      <section
        className={`promotion-popup-card ${
          promotion.imageUrl ? "has-image" : "text-only"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="promotion-popup-title"
        aria-describedby={promotion.message ? "promotion-popup-message" : undefined}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/*<button
          type="button"
          className="promotion-popup-close"
          onClick={closePopup}
          aria-label="Close promotion"
        >
          <i className="pi pi-times" aria-hidden="true" />
        </button>*/}

        {promotion.imageUrl && (
          <div className="promotion-popup-image-wrap">
            {promotion.imageClickUrl ? (
              <button
                type="button"
                className="promotion-popup-image-link"
                onClick={() =>
                  followLink(promotion.imageClickUrl, "image_click")
                }
                aria-label={`Open ${promotion.title}`}
              >
                <img src={promotion.imageUrl} alt={promotion.title} />
              </button>
            ) : (
              <img src={promotion.imageUrl} alt="" />
            )}
          </div>
        )}

        <div className="promotion-popup-content">
          <p className="promotion-popup-eyebrow">A note from Graphe Weddings</p>
          <h2 id="promotion-popup-title">{promotion.title}</h2>

          {promotion.message && (
            <p id="promotion-popup-message" className="promotion-popup-message">
              {promotion.message}
            </p>
          )}

          {Array.isArray(promotion.buttons) && promotion.buttons.length > 0 && (
            <div className="promotion-popup-actions">
              {promotion.buttons.map((button, index) => (
                <button
                  type="button"
                  className={`promotion-popup-action ${button.style || "primary"}`}
                  key={`${button.label}-${index}`}
                  onClick={() => followLink(button.url, "button_click", index)}
                >
                  {button.label}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            className="promotion-popup-dismiss"
            onClick={closePopup}
          >
            Maybe later
          </button>
        </div>
      </section>
    </div>
  );
};

export default PromotionalPopup;
